import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { Btn, Card, DataTable, Pill, Select, type Col } from '../../shared/ui';
import {
    Check, Field, FormGrid, Input, Note, OUTCOME_LABEL, Section, day, dayTime, tone, yes,
    type Intermediary, type Lead, type Props,
} from './posrShared';

/** m.1.2 Prijave leadova: unos prije prve kupnje, provjera (prva prijava, poznat klijent), odluka. */

type LeadCheck = { suggested: string; reasons: string[] };

const EMPTY = { intermediary_id: '', client_name: '', client_oib: '', client_email: '', client_phone: '', reported_at: '', customer_code: '', note: '' };

export function PosrLeads({ token, isAdmin, onError }: Props) {
    const [rows, setRows] = useState<Lead[] | null>(null);
    const [intermediaries, setIntermediaries] = useState<Intermediary[]>([]);
    const [outcome, setOutcome] = useState('');
    const [form, setForm] = useState({ ...EMPTY });
    const [consent, setConsent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [sel, setSel] = useState<Lead | null>(null);
    const [selCheck, setSelCheck] = useState<LeadCheck | null>(null);
    const [code, setCode] = useState('');
    const [decisionNote, setDecisionNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setRows(null);
        api<{ rows: Lead[] }>(`/posrednici/leads${outcome ? `?outcome=${outcome}` : ''}`, token)
            .then((d) => setRows(d.rows)).catch((e: Error) => { onError(e.message); setRows([]); });
    }, [token, outcome, onError]);
    useEffect(load, [load]);
    useEffect(() => {
        api<{ rows: Intermediary[] }>('/posrednici/intermediaries', token).then((d) => setIntermediaries(d.rows)).catch(() => undefined);
    }, [token]);

    const openLead = (l: Lead) => {
        setSel(l); setCode(l.customer_code ?? ''); setDecisionNote(''); setSelCheck(null);
        api<LeadCheck>(`/posrednici/leads/${l.id}/check`, token).then(setSelCheck).catch((e: Error) => onError(e.message));
    };

    const run = async (fn: () => Promise<unknown>) => {
        setBusy(true);
        try { await fn(); load(); } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
    };

    const submit = () => run(async () => {
        const body: Record<string, unknown> = { consent_given: consent };
        for (const [k, v] of Object.entries(form)) body[k] = v === '' ? null : v;
        body.intermediary_id = Number(form.intermediary_id);
        const res = await mutate<{ check: LeadCheck }>('/posrednici/leads', token, 'POST', body);
        setShowNew(false); setForm({ ...EMPTY }); setConsent(false);
        if (res.check.suggested !== 'prihvacen') onError(`Prijava je upisana, a provjera predlaže "${OUTCOME_LABEL[res.check.suggested]}". ${res.check.reasons.join(' ')}`);
    });

    const linkCustomer = () => sel && run(async () => {
        const res = await mutate<{ check: LeadCheck }>(`/posrednici/leads/${sel.id}`, token, 'PATCH', { customer_code: code || null });
        setSelCheck(res.check);
    });

    const decide = (o: string) => sel && run(async () => {
        await mutate(`/posrednici/leads/${sel.id}/decide`, token, 'POST', { outcome: o, note: decisionNote || null });
        setSel(null);
    });

    const active = intermediaries.filter((i) => i.status === 'aktivan' || i.status === 'probni');
    const today = new Date().toISOString().slice(0, 10);

    const cols: Col<Lead>[] = [
        { key: 'reported_at', label: 'Prijavljeno', render: (r) => dayTime(r.reported_at) },
        { key: 'intermediary_name', label: 'Posrednik' },
        { key: 'client_name', label: 'Klijent', render: (r) => <div><div style={{ fontWeight: 650 }}>{r.client_name}</div><div style={{ fontSize: '0.75rem', color: '#8a90a3' }}>{[r.client_email, r.client_phone].filter(Boolean).join(' · ')}</div></div> },
        { key: 'outcome', label: 'Ishod', render: (r) => <Pill tone={tone(r.outcome)}>{OUTCOME_LABEL[r.outcome] ?? r.outcome}</Pill> },
        { key: 'decision_due_at', label: 'Odluka do', render: (r) => r.outcome === 'ceka' && r.decision_due_at && r.decision_due_at < today ? <Pill tone="bad">{day(r.decision_due_at)}</Pill> : day(r.decision_due_at) },
        { key: 'customer_code', label: 'Šifra kupca', render: (r) => r.customer_code ?? '-' },
        { key: 'first_purchase_at', label: 'Prva kupnja', render: (r) => day(r.first_purchase_at) },
        { key: 'commission_until', label: 'Provizija do', render: (r) => day(r.commission_until) },
    ];

    return (
        <>
            <Card>
                <Section title="Prijave leadova" actions={<>
                    <Select value={outcome} onChange={setOutcome}>
                        <option value="">Svi ishodi</option>
                        {Object.entries(OUTCOME_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </Select>
                    <Btn variant="primary" onClick={() => setShowNew((s) => !s)}>Nova prijava</Btn>
                </>}>
                    {showNew ? (
                        <Card pad={14}>
                            <FormGrid>
                                <Field label="Posrednik">
                                    <Select value={form.intermediary_id} onChange={(v) => setForm({ ...form, intermediary_id: v })} width="100%">
                                        <option value="">Odaberite</option>
                                        {active.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Ime i prezime klijenta"><Input value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} /></Field>
                                <Field label="OIB klijenta"><Input value={form.client_oib} onChange={(v) => setForm({ ...form, client_oib: v })} /></Field>
                                <Field label="E-mail"><Input value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} /></Field>
                                <Field label="Telefon"><Input value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v })} /></Field>
                                <Field label="Datum i sat prijave (prazno je sada)"><Input type="datetime-local" value={form.reported_at} onChange={(v) => setForm({ ...form, reported_at: v })} /></Field>
                                <Field label="Šifra kupca u BIS-u, ako postoji"><Input value={form.customer_code} onChange={(v) => setForm({ ...form, customer_code: v })} /></Field>
                            </FormGrid>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
                                <Check label="Posrednik potvrđuje privolu osobe" checked={consent} onChange={setConsent} />
                                <Btn variant="primary" disabled={busy || !form.intermediary_id || !form.client_name} onClick={submit}>Upiši prijavu</Btn>
                            </div>
                        </Card>
                    ) : null}
                    <DataTable cols={cols} rows={rows} onRowClick={openLead} empty="Nema prijava." />
                </Section>
            </Card>

            {sel ? (
                <Card>
                    <Section title={`Prijava ${sel.client_name}, posrednik ${sel.intermediary_name}`} actions={<Btn onClick={() => setSel(null)}>Zatvori</Btn>}>
                        <Note>
                            Prijava je dana {dayTime(sel.reported_at)} i vrijedi do {day(sel.expires_at)}, a privola {yes(sel.consent_given) ? 'je potvrđena' : 'nije potvrđena'}.
                            {selCheck ? ` Provjera predlaže ishod "${OUTCOME_LABEL[selCheck.suggested]}". ${selCheck.reasons.join(' ')}` : ' Provjera se učitava.'}
                        </Note>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={{ width: 220 }}><Field label="Šifra kupca u BIS-u"><Input value={code} onChange={setCode} /></Field></div>
                            <Btn disabled={busy} onClick={linkCustomer}>Poveži kupca i provjeri</Btn>
                        </div>
                        {isAdmin ? (
                            <>
                                <Field label="Obrazloženje odluke" wide><Input value={decisionNote} onChange={setDecisionNote} /></Field>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {Object.keys(OUTCOME_LABEL).filter((o) => o !== 'ceka').map((o) => (
                                        <Btn key={o} variant={o === 'prihvacen' ? 'primary' : 'ghost'} disabled={busy} onClick={() => decide(o)}>{OUTCOME_LABEL[o]}</Btn>
                                    ))}
                                </div>
                            </>
                        ) : <Note>Odluku o prijavi upisuje administrator.</Note>}
                    </Section>
                </Card>
            ) : null}
        </>
    );
}
